import { createContext, useContext, useState, useEffect } from 'react';

type ConsentValue = 'accepted' | 'rejected' | 'unknown';

type CookieConsentContextType = {
    consent: ConsentValue;
    acceptCookies: () => void;
    rejectCookies: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
    const [consent, setConsent] = useState<ConsentValue>('unknown');

    // Load consent from cookies on mount
    useEffect(() => {
        const cookieConsent = document.cookie
            .split('; ')
            .find((row) => row.startsWith('refundr-cookie-consent='))
            ?.split('=')[1] as ConsentValue;
        setConsent(cookieConsent || 'unknown');
    }, []);

    const acceptCookies = () => {
        document.cookie = 'refundr-cookie-consent=accepted; path=/; max-age=31536000'; // 1 year
        setConsent('accepted');
    };

    const rejectCookies = () => {
        document.cookie = 'refundr-cookie-consent=rejected; path=/; max-age=31536000'; // 1 year
        setConsent('rejected');
    };

    return (
        <CookieConsentContext.Provider value={{ consent, acceptCookies, rejectCookies }}>
            {children}
        </CookieConsentContext.Provider>
    );
}

export function useCookieConsent() {
    const context = useContext(CookieConsentContext);
    if (!context) {
        throw new Error('useCookieConsent must be used within a CookieConsentProvider');
    }
    return context;
}
